import type { ConsoleOrganization } from '@sentinel/shared';
import { Button, InputField, Modal } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StatusChange } from './use-organization';

const K = 'console.organization.status';

interface Props {
  organization: ConsoleOrganization;
  change: StatusChange;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** The consequences of a change of state (rule 17); archiving asks for the slug, since the
 *  organization will not open again unless the platform reactivates it. */
export function StatusChangeModal({ organization, change, busy, onClose, onConfirm }: Props) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const needsWord = change === 'archive';
  const confirmed = !needsWord || typed.trim() === organization.slug;
  const consequences = t(`${K}.confirm.${change}.consequences`, {
    returnObjects: true,
  }) as string[];

  return (
    <Modal
      open
      title={t(`${K}.confirm.${change}.title`, { name: organization.name })}
      icon="warning"
      onClose={onClose}
      closeLabel={t('common.actions.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
          <Button
            variant={change === 'reactivate' ? 'primary' : 'danger'}
            disabled={!confirmed || busy}
            onClick={onConfirm}
          >
            {t(`${K}.actions.${change}`)}
          </Button>
        </>
      }
    >
      <ul className="consequences">
        {consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {needsWord ? (
        <InputField
          id="archive-slug"
          label={t(`${K}.typeSlug`)}
          placeholder={organization.slug}
          value={typed}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
        />
      ) : null}
    </Modal>
  );
}
